'use client';

import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Check, Trash2, Loader2 } from 'lucide-react';
import Image from 'next/image';
import { cn } from '@/doi-tac-module/lib/utils';

interface AvatarHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentAvatarUrl: string;
    avatarHistory: string[];
    onSelectAvatar: (url: string) => Promise<void>;
    onDeleteAvatar: (url: string) => Promise<void>;
    isProcessing: boolean;
    t: (key: string) => string;
}

export function AvatarHistoryModal({
    isOpen,
    onClose,
    currentAvatarUrl,
    avatarHistory = [],
    onSelectAvatar,
    onDeleteAvatar,
    isProcessing,
    t
}: AvatarHistoryModalProps) {
    // Combine current and history for display (unique URLs)
    const allAvatars = Array.from(new Set([currentAvatarUrl, ...avatarHistory])).filter(Boolean);

    const handleSelect = async (url: string) => {
        if (url === currentAvatarUrl) return;
        await onSelectAvatar(url);
        onClose();
    };

    const handleDelete = async (e: React.MouseEvent, url: string) => {
        e.stopPropagation();
        if (allAvatars.length <= 1) return; // Cannot delete the last one
        await onDeleteAvatar(url);
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-[600px] bg-white max-h-[80vh] flex flex-col p-0 overflow-hidden">
                <DialogHeader className="p-6 pb-2 border-b">
                    <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                        {t('avatarHistoryTitle') || 'Lịch sử ảnh đại diện'}
                        <span className="text-sm font-normal text-muted-foreground bg-slate-100 px-2 py-0.5 rounded-full">
                            {allAvatars.length}/10
                        </span>
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-grow overflow-y-auto p-6">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {allAvatars.map((url, index) => {
                            const isCurrent = url === currentAvatarUrl;
                            return (
                                <div
                                    key={`${url}-${index}`}
                                    className={cn(
                                        "relative aspect-square rounded-xl overflow-hidden border-2 transition-all group cursor-pointer",
                                        isCurrent ? "border-primary ring-2 ring-primary/20 shadow-lg" : "border-slate-200 hover:border-primary/50 hover:shadow-md"
                                    )}
                                    onClick={() => handleSelect(url)}
                                >
                                    <Image
                                        src={url}
                                        alt={`Avatar ${index + 1}`}
                                        fill
                                        className={cn(
                                            "object-cover transition-transform duration-500",
                                            !isCurrent && "group-hover:scale-110"
                                        )}
                                    />
                                    
                                    {isCurrent && (
                                        <div className="absolute top-2 right-2 bg-primary text-white p-1 rounded-full shadow-md z-10 animate-in zoom-in duration-300">
                                            <Check className="h-3 w-3" />
                                        </div>
                                    )}

                                    {!isCurrent && (
                                        <button
                                            className="absolute top-2 right-2 bg-white/90 hover:bg-red-500 hover:text-white text-slate-500 p-2 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-all z-20"
                                            onClick={(e) => handleDelete(e, url)}
                                            disabled={isProcessing}
                                            title={t('delete') || 'Xóa'}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}

                                    {!isCurrent && (
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
                                    )}
                                    
                                    {isCurrent && (
                                        <div className="absolute bottom-0 left-0 right-0 bg-primary/90 text-white text-[10px] uppercase font-bold py-1 text-center backdrop-blur-sm">
                                            {t('currentLabel') || 'Đang dùng'}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <DialogFooter className="p-4 border-t bg-slate-50">
                    <Button variant="ghost" onClick={onClose} disabled={isProcessing}>
                        {t('close') || 'Đóng'}
                    </Button>
                </DialogFooter>

                {isProcessing && (
                    <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] flex items-center justify-center z-50">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
